import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Upload,
  Tag,
  Row,
  Col,
  Statistic,
  Popconfirm,
  Tooltip,
  Icon,
  Divider,
  Alert,
  Checkbox,
  Progress
} from 'antd';
import moment from 'moment';
import {
  getAllAssetModels,
  saveAssetModel,
  saveMultipleAssetModels,
  getUniqueClassifications,
  generateSubTypeCode,
  exportForAI,
  importAISuggestions,
  getAISuggestions,
  clearAISuggestions
} from './utils/assetClassificationDB';
import { loadAssetModels } from './utils/assetModelLoader';
import { loadAssetClassificationData } from './utils/assetClassificationLoader';

const { Option } = Select;
const { TextArea } = Input;

function AssetClassificationManager({ form: mainForm }) {
  const [assetModels, setAssetModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [aiModalVisible, setAiModalVisible] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState(new Set());
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiPageSize, setAiPageSize] = useState(10);
  const [aiCurrentPage, setAiCurrentPage] = useState(1);
  const [uniqueClassifications, setUniqueClassifications] = useState({
    classifications: [],
    types: [],
    subTypes: []
  });

  useEffect(() => {
    // Load with cache-busting to ensure fresh data
    loadAssetModelsData(true);
  }, []);

  const loadAssetModelsData = async (forceReload = false) => {
    setLoading(true);
    try {
      // Always load from Asset Classification CSV
      try {
        console.log('Loading asset classifications from Asset Classification.csv...');
        const csvModels = await loadAssetClassificationData(forceReload);
        console.log(`Loaded ${csvModels.length} models from CSV`);
        
        // Generate codes for sub types if not present
          csvModels.forEach(model => {
            if (model.assetSubType && !model.assetSubTypeCode) {
              model.assetSubTypeCode = generateSubTypeCode(model.assetSubType);
            }
          });
        
        // Clear existing data and save new data from CSV
        // First, clear the database by initializing it
        const db = JSON.parse(localStorage.getItem('asset_classification_db') || '{}');
        const oldCount = (db.assetModels || []).length;
        db.assetModels = [];
        db._updatedAt = new Date().toISOString();
        localStorage.setItem('asset_classification_db', JSON.stringify(db));
        console.log(`Cleared ${oldCount} old models from database`);
        
        // Save new data to database
          saveMultipleAssetModels(csvModels);
        console.log(`Saved ${csvModels.length} new models to database`);
        
        setAssetModels(csvModels);
        updateUniqueClassifications(csvModels);
        message.success(`Loaded ${csvModels.length} asset classifications from Asset Classification.csv`);
        } catch (error) {
        console.error('Error loading from Asset Classification CSV:', error);
        // If CSV loading fails, try to load from database as fallback
        const models = getAllAssetModels();
        if (models.length > 0) {
      setAssetModels(models);
      updateUniqueClassifications(models);
          message.warning('Using existing database data. CSV load failed.');
        } else {
          message.error('Failed to load asset classifications from CSV');
        }
      }
    } catch (error) {
      console.error('Error loading asset models:', error);
      message.error('Failed to load asset models');
    } finally {
      setLoading(false);
    }
  };

  const updateUniqueClassifications = (models) => {
    const unique = getUniqueClassifications();
    setUniqueClassifications(unique);
  };

  const handleEdit = (record) => {
    setEditingModel(record);
    mainForm.setFieldsValue({
      assetModel: record.assetModel,
      assetClassification: record.assetClassification || '',
      assetType: record.assetType || '',
      assetSubType: record.assetSubType || '',
      assetSubTypeCode: record.assetSubTypeCode || ''
    });
    setEditModalVisible(true);
  };

  const handleSave = () => {
    mainForm.validateFields((err, values) => {
      if (!err) {
        const updatedModel = {
          ...editingModel,
          ...values,
          // Auto-generate code if sub type changed
          assetSubTypeCode: values.assetSubTypeCode || generateSubTypeCode(values.assetSubType)
        };
        
        saveAssetModel(updatedModel);
        message.success('Asset model updated successfully');
        setEditModalVisible(false);
        loadAssetModelsData();
      }
    });
  };

  const handleSubTypeChange = (value) => {
    // Auto-generate code when sub type changes
    const code = generateSubTypeCode(value);
    mainForm.setFieldsValue({
      assetSubTypeCode: code
    });
  };

  const handleExportForAI = () => {
    try {
      const data = exportForAI();
      const dataStr = JSON.stringify(data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `asset-models-for-ai-${moment().format('YYYY-MM-DD')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success('Data exported for AI processing');
    } catch (error) {
      console.error('Error exporting:', error);
      message.error('Failed to export data');
    }
  };

  const handleImportAISuggestions = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          
          // Handle both array format and object with array property
          let suggestions = Array.isArray(data) ? data : (data.suggestions || data.data || []);
          
          // Validate structure
          if (!Array.isArray(suggestions) || suggestions.length === 0) {
            reject(new Error('Invalid file format. Expected JSON array or object with suggestions array.'));
            return;
          }
          
          // Map suggestions to match expected format if needed
          suggestions = suggestions.map(suggestion => {
            // If suggestion has id, use it; otherwise try to match by assetModel
            if (!suggestion.id && suggestion.assetModel) {
              const model = assetModels.find(m => m.assetModel === suggestion.assetModel);
              if (model) {
                suggestion.id = model.id;
              }
            }
            return suggestion;
          });
          
          // Import suggestions
          importAISuggestions(suggestions);
          setAiSuggestions(suggestions);
          setSelectedSuggestions(new Set()); // Clear previous selections
          setAiModalVisible(true);
          message.success(`Loaded ${suggestions.length} AI suggestions`);
          resolve();
        } catch (parseError) {
          reject(parseError);
        }
      };
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsText(file);
    });
  };

  const handleProcessWithAI = async () => {
    setAiProcessing(true);
    try {
      // Export data
      const data = exportForAI();
      
      // In a real implementation, this would call an AI API
      // For now, we'll simulate AI processing
      message.info('Processing with AI... This may take a few moments.');
      
      // Simulate AI processing delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock AI suggestions (in real implementation, this would come from API)
      // Map each exported item to its corresponding model to get the id
      const mockSuggestions = data.map(item => {
        // Find the model by matching id (which is now in the export)
        const model = assetModels.find(m => m.id === item.id);
        return {
          id: item.id,
          assetModel: item.assetModel,
          currentClassification: item.currentClassification,
          currentType: item.currentType,
          currentSubType: item.currentSubType,
          currentSubTypeCode: item.currentSubTypeCode,
        suggestedClassification: item.currentClassification || 'Equipment',
        suggestedType: item.currentType || 'General',
        suggestedSubType: item.currentSubType || 'Standard',
        suggestedSubTypeCode: generateSubTypeCode(item.currentSubType || 'Standard'),
        confidence: Math.random() * 0.3 + 0.7, // 70-100% confidence
        reasoning: 'AI analysis suggests this classification based on asset model and existing patterns.'
        };
      });
      
      importAISuggestions(mockSuggestions);
      setAiSuggestions(mockSuggestions);
      setAiModalVisible(true);
      message.success('AI processing complete! Review suggestions below.');
    } catch (error) {
      console.error('Error processing with AI:', error);
      message.error('Failed to process with AI');
    } finally {
      setAiProcessing(false);
    }
  };

  const handleAcceptSuggestion = (suggestion, acceptAll = false) => {
    if (acceptAll) {
      // Accept all selected suggestions
      const toAccept = aiSuggestions.filter((_, index) => selectedSuggestions.has(index));
      toAccept.forEach(suggestion => {
        const model = assetModels.find(m => m.id === suggestion.id);
        if (model) {
          const updated = {
            ...model,
            assetClassification: suggestion.suggestedClassification,
            assetType: suggestion.suggestedType,
            assetSubType: suggestion.suggestedSubType,
            assetSubTypeCode: suggestion.suggestedSubTypeCode
          };
          saveAssetModel(updated);
        }
      });
      message.success(`Accepted ${toAccept.length} AI suggestions`);
      setSelectedSuggestions(new Set());
      loadAssetModelsData();
    } else {
      // Accept single suggestion
      const model = assetModels.find(m => m.id === suggestion.id);
      if (model) {
        const updated = {
          ...model,
          assetClassification: suggestion.suggestedClassification,
          assetType: suggestion.suggestedType,
          assetSubType: suggestion.suggestedSubType,
          assetSubTypeCode: suggestion.suggestedSubTypeCode
        };
        saveAssetModel(updated);
        message.success('Suggestion accepted');
        loadAssetModelsData();
      }
    }
  };

  const handleSelectSuggestion = (index) => {
    const newSelected = new Set(selectedSuggestions);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedSuggestions(newSelected);
  };

  const handleSelectAllSuggestions = () => {
    if (selectedSuggestions.size === aiSuggestions.length) {
      setSelectedSuggestions(new Set());
    } else {
      setSelectedSuggestions(new Set(aiSuggestions.map((_, i) => i)));
    }
  };

  const handleSelectAllOnPage = () => {
    // Calculate indices for current page
    const startIndex = (aiCurrentPage - 1) * aiPageSize;
    const endIndex = Math.min(startIndex + aiPageSize, aiSuggestions.length);
    const currentPageIndices = [];
    
    for (let i = startIndex; i < endIndex; i++) {
      currentPageIndices.push(i);
    }
    
    const newSelected = new Set(selectedSuggestions);
    const allOnPageSelected = currentPageIndices.length > 0 && 
      currentPageIndices.every(idx => newSelected.has(idx));
    
    if (allOnPageSelected) {
      // Deselect all on current page
      currentPageIndices.forEach(idx => newSelected.delete(idx));
    } else {
      // Select all on current page
      currentPageIndices.forEach(idx => newSelected.add(idx));
    }
    
    setSelectedSuggestions(newSelected);
  };

  const handleAcceptAllSuggestions = () => {
    aiSuggestions.forEach(suggestion => {
      const model = assetModels.find(m => m.id === suggestion.id);
      if (model) {
        const updated = {
          ...model,
          assetClassification: suggestion.suggestedClassification,
          assetType: suggestion.suggestedType,
          assetSubType: suggestion.suggestedSubType,
          assetSubTypeCode: suggestion.suggestedSubTypeCode
        };
        saveAssetModel(updated);
      }
    });
    message.success(`Accepted all ${aiSuggestions.length} AI suggestions`);
    setSelectedSuggestions(new Set());
    loadAssetModelsData();
    setAiModalVisible(false);
  };

  const columns = [
    {
      title: 'Asset Model',
      dataIndex: 'assetModel',
      key: 'assetModel',
      width: 250,
      render: (text) => text || <span style={{ color: '#999' }}>N/A</span>
    },
    {
      title: 'Classification',
      dataIndex: 'assetClassification',
      key: 'assetClassification',
      width: 150,
      render: (text) => text ? <Tag>{text}</Tag> : <span style={{ color: '#999' }}>Not Set</span>
    },
    {
      title: 'Asset Type',
      dataIndex: 'assetType',
      key: 'assetType',
      width: 180,
      render: (text) => text ? <Tag color="blue">{text}</Tag> : <span style={{ color: '#999' }}>N/A</span>
    },
    {
      title: 'Asset Sub Type',
      dataIndex: 'assetSubType',
      key: 'assetSubType',
      width: 200,
      render: (text) => text ? <Tag color="green">{text}</Tag> : <span style={{ color: '#999' }}>N/A</span>
    },
    {
      title: 'Sub Type Code',
      dataIndex: 'assetSubTypeCode',
      key: 'assetSubTypeCode',
      width: 120,
      render: (text) => text ? <Tag color="orange">{text}</Tag> : <span style={{ color: '#999' }}>N/A</span>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          icon="edit"
          size="small"
          onClick={() => handleEdit(record)}
        >
          Edit
        </Button>
      )
    }
  ];

  const totalModels = assetModels.length;
  const classifiedCount = assetModels.filter(m => m.assetClassification && m.assetType && m.assetSubType).length;
  const unclassifiedCount = totalModels - classifiedCount;

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Asset Models"
                value={totalModels}
                prefix={<Icon type="database" />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Classified"
                value={classifiedCount}
                prefix={<Icon type="check-circle" />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Unclassified"
                value={unclassifiedCount}
                prefix={<Icon type="exclamation-circle" />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      <Card
        title={
          <span>
            <Icon type="appstore" style={{ marginRight: 8 }} />
            Asset Classification Management
          </span>
        }
        extra={
          <div>
            <Button
              icon="reload"
              onClick={() => loadAssetModelsData(true)}
              style={{ marginRight: 8 }}
              title="Reload from CSV file"
            >
              Reload from CSV
            </Button>
            <Button
              icon="download"
              onClick={handleExportForAI}
              style={{ marginRight: 8 }}
            >
              Export for AI
            </Button>
            <Upload
              accept=".json"
              showUploadList={false}
              beforeUpload={handleImportAISuggestions}
            >
              <Button icon="upload" style={{ marginRight: 8 }}>
                Import AI Classified File
              </Button>
            </Upload>
            <Button
              type="primary"
              icon="thunderbolt"
              onClick={handleProcessWithAI}
              loading={aiProcessing}
            >
              Process with AI
            </Button>
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={assetModels}
          rowKey="id"
          loading={loading}
          scroll={{ x: 960 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} asset models`
          }}
        />
      </Card>

      {/* Edit Modal */}
      <Modal
        title="Edit Asset Classification"
        visible={editModalVisible}
        onOk={handleSave}
        onCancel={() => setEditModalVisible(false)}
        width={600}
        okText="Save"
        cancelText="Cancel"
      >
        <Form layout="vertical">
          <Form.Item label="Asset Model">
            {mainForm.getFieldDecorator('assetModel')(
              <Input disabled />
            )}
          </Form.Item>
          <Form.Item label="Asset Classification (Optional)">
            {mainForm.getFieldDecorator('assetClassification')(
              <Select
                placeholder="Select or enter classification"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {uniqueClassifications.classifications.map(c => (
                  <Option key={c} value={c}>{c}</Option>
                ))}
              </Select>
            )}
          </Form.Item>
          <Form.Item label="Asset Type">
            {mainForm.getFieldDecorator('assetType', {
              rules: [{ required: true, message: 'Please enter asset type' }]
            })(
              <Select
                placeholder="Select or enter asset type"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {uniqueClassifications.types.map(t => (
                  <Option key={t} value={t}>{t}</Option>
                ))}
              </Select>
            )}
          </Form.Item>
          <Form.Item label="Asset Sub Type">
            {mainForm.getFieldDecorator('assetSubType', {
              rules: [{ required: true, message: 'Please enter asset sub type' }]
            })(
              <Select
                placeholder="Select or enter asset sub type"
                showSearch
                allowClear
                onChange={handleSubTypeChange}
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {uniqueClassifications.subTypes.map(st => (
                  <Option key={st} value={st}>{st}</Option>
                ))}
              </Select>
            )}
          </Form.Item>
          <Form.Item label="Sub Type Code">
            {mainForm.getFieldDecorator('assetSubTypeCode', {
              rules: [{ required: true, message: 'Please enter sub type code' }]
            })(
              <Input
                placeholder="Auto-generated from Sub Type name"
                maxLength={3}
                style={{ textTransform: 'uppercase' }}
              />
            )}
            <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
              Code generation: 1 word = first 3 letters, 2 words = 2+1 letters, 3+ words = first letter of first 3 words
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* AI Suggestions Modal */}
      <Modal
        title={
          <div>
            <Icon type="thunderbolt" style={{ marginRight: 8 }} />
            AI Reclassification Suggestions
            <Tag color="blue" style={{ marginLeft: 16 }}>
              {selectedSuggestions.size} selected
            </Tag>
          </div>
        }
        visible={aiModalVisible}
        onCancel={() => {
          setAiModalVisible(false);
          setSelectedSuggestions(new Set());
        }}
        width={1200}
        footer={[
          <Button key="cancel" onClick={() => {
            setAiModalVisible(false);
            setSelectedSuggestions(new Set());
          }}>
            Close
          </Button>,
          <Button
            key="select-all"
            onClick={handleSelectAllSuggestions}
          >
            {selectedSuggestions.size === aiSuggestions.length ? 'Deselect All' : 'Select All'}
          </Button>,
          <Button
            key="accept-selected"
            type="primary"
            disabled={selectedSuggestions.size === 0}
            onClick={() => handleAcceptSuggestion(null, true)}
          >
            Accept Selected ({selectedSuggestions.size})
          </Button>,
          <Button
            key="accept-all"
            type="primary"
            onClick={handleAcceptAllSuggestions}
            style={{ marginLeft: 8 }}
          >
            Accept All ({aiSuggestions.length})
          </Button>
        ]}
      >
        <Alert
          message="AI Reclassification Results"
          description="Review the AI suggestions below. You can accept individual suggestions, select multiple and accept them, or accept all at once."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Button
              size="small"
              onClick={handleSelectAllOnPage}
              icon="check-square"
            >
              Select All on Page
            </Button>
            <span style={{ marginLeft: 16, color: '#666' }}>
              {selectedSuggestions.size} of {aiSuggestions.length} selected
            </span>
          </div>
          <div>
            <span style={{ marginRight: 8 }}>Page Size:</span>
            <Select
              value={aiPageSize}
              onChange={(value) => {
                setAiPageSize(value);
                setAiCurrentPage(1); // Reset to first page when changing page size
              }}
              style={{ width: 80 }}
              size="small"
            >
              <Option value={10}>10</Option>
              <Option value={20}>20</Option>
              <Option value={50}>50</Option>
              <Option value={100}>100</Option>
            </Select>
          </div>
        </div>
        <Table
          columns={[
            {
              title: (
                <Checkbox
                  indeterminate={
                    selectedSuggestions.size > 0 && 
                    selectedSuggestions.size < aiSuggestions.length
                  }
                  checked={
                    aiSuggestions.length > 0 && 
                    selectedSuggestions.size === aiSuggestions.length
                  }
                  onChange={handleSelectAllSuggestions}
                />
              ),
              key: 'select',
              width: 50,
              fixed: 'left',
              render: (_, record, index) => (
                <Checkbox
                  checked={selectedSuggestions.has(index)}
                  onChange={() => handleSelectSuggestion(index)}
                />
              )
            },
            {
              title: 'Asset Model',
              dataIndex: 'assetModel',
              key: 'assetModel',
              width: 200
            },
            {
              title: 'Current Classification',
              key: 'current',
              width: 220,
              render: (_, record) => {
                const hasChanges = 
                  record.currentClassification !== record.suggestedClassification ||
                  record.currentType !== record.suggestedType ||
                  record.currentSubType !== record.suggestedSubType;
                
                return (
                  <div style={{ 
                    padding: '8px',
                    backgroundColor: hasChanges ? '#fff7e6' : '#f0f0f0',
                    borderRadius: '4px'
                  }}>
                  <div><Tag>{record.currentClassification || 'N/A'}</Tag></div>
                  <div style={{ fontSize: '12px', marginTop: 4 }}>
                    <Tag color="blue">{record.currentType || 'N/A'}</Tag>
                  </div>
                  <div style={{ fontSize: '12px', marginTop: 4 }}>
                    <Tag color="green">{record.currentSubType || 'N/A'}</Tag>
                  </div>
                    <div style={{ fontSize: '12px', marginTop: 4 }}>
                      <Tag color="orange">Code: {record.currentSubTypeCode || 'N/A'}</Tag>
                    </div>
                </div>
                );
              }
            },
            {
              title: '→',
              key: 'arrow',
              width: 30,
              render: () => <Icon type="arrow-right" />
            },
            {
              title: 'AI Suggested Classification',
              key: 'suggested',
              width: 220,
              render: (_, record) => {
                const hasChanges = 
                  record.currentClassification !== record.suggestedClassification ||
                  record.currentType !== record.suggestedType ||
                  record.currentSubType !== record.suggestedSubType;
                
                return (
                  <div style={{ 
                    padding: '8px',
                    backgroundColor: hasChanges ? '#e6f7ff' : '#f0f0f0',
                    borderRadius: '4px'
                  }}>
                  <div><Tag color="purple">{record.suggestedClassification || 'N/A'}</Tag></div>
                  <div style={{ fontSize: '12px', marginTop: 4 }}>
                    <Tag color="cyan">{record.suggestedType || 'N/A'}</Tag>
                  </div>
                  <div style={{ fontSize: '12px', marginTop: 4 }}>
                    <Tag color="lime">{record.suggestedSubType || 'N/A'}</Tag>
                  </div>
                  <div style={{ fontSize: '12px', marginTop: 4 }}>
                    <Tag color="orange">Code: {record.suggestedSubTypeCode || 'N/A'}</Tag>
                  </div>
                </div>
                );
              }
            },
            {
              title: 'Confidence',
              dataIndex: 'confidence',
              key: 'confidence',
              width: 120,
              render: (confidence) => (
                <div>
                  <Progress
                    percent={Math.round(confidence * 100)}
                    size="small"
                    status={confidence > 0.8 ? 'success' : confidence > 0.6 ? 'normal' : 'exception'}
                  />
                  <div style={{ fontSize: '12px', marginTop: 4 }}>
                    {Math.round(confidence * 100)}%
                  </div>
                </div>
              )
            },
            {
              title: 'Reasoning',
              dataIndex: 'reasoning',
              key: 'reasoning',
              width: 250,
              ellipsis: true
            },
            {
              title: 'Action',
              key: 'action',
              width: 100,
              render: (_, record) => (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleAcceptSuggestion(record)}
                >
                  Accept
                </Button>
              )
            }
          ]}
          dataSource={aiSuggestions}
          rowKey={(record, index) => record.id || index}
          pagination={{
            current: aiCurrentPage,
            pageSize: aiPageSize,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} suggestions`,
            onChange: (page, pageSize) => {
              setAiCurrentPage(page);
              if (pageSize !== aiPageSize) {
                setAiPageSize(pageSize);
              }
            },
            onShowSizeChange: (current, size) => {
              setAiPageSize(size);
              setAiCurrentPage(1); // Reset to first page when changing size
            }
          }}
          scroll={{ y: 500, x: 1200 }}
        />
      </Modal>
    </div>
  );
}

export default Form.create({ name: 'asset_classification_manager' })(AssetClassificationManager);

